export function buildBreakoutDisplayName({
    firstName = "",
    lastName = "",
    registrantCode = "",
    country = "",
}) {
    const name = `${firstName || ""} ${lastName || ""}`.trim();
    const code = `${registrantCode || ""}`.trim();
    const countryLabel = `${country || ""}`.trim();

    if (!name || !code) {
        return "";
    }

    if (countryLabel) {
        return `${name} ${code} - ${countryLabel}`;
    }

    return `${name} ${code}`;
}

/**
 * Flatten rooms + main-session participants from breakout participants API.
 */
export function flattenBreakoutParticipants(payload = {}) {
    const people = [];
    const rooms = payload.rooms || [];
    const main = payload.participants || [];

    rooms.forEach((room) => {
        (room.participants || []).forEach((participant) => {
            people.push({
                userId: participant.userId,
                displayName: participant.displayName,
                currentRoomId: participant.currentRoomId || room.roomId,
                currentRoomName: participant.currentRoomName || room.name,
            });
        });
    });

    main.forEach((participant) => {
        people.push({
            userId: participant.userId,
            displayName: participant.displayName,
            currentRoomId: participant.currentRoomId,
            currentRoomName: participant.currentRoomName,
        });
    });

    return people;
}

/**
 * Prefer matching Zoom displayName by registrant unique id (case-insensitive).
 * Falls back to built displayName substring match.
 */
export function findBreakoutParticipant({
    participants = [],
    registrantCode = "",
    fallbackDisplayName = "",
}) {
    const code = `${registrantCode || ""}`.trim().toLowerCase();
    const fallback = `${fallbackDisplayName || ""}`.trim().toLowerCase();

    if (code) {
        const byCode = participants.find((participant) => {
            const displayName = `${participant.displayName || ""}`.toLowerCase();
            return displayName.includes(code);
        });
        if (byCode) {
            return byCode;
        }
    }

    if (fallback) {
        return participants.find((participant) => {
            const displayName = `${participant.displayName || ""}`.toLowerCase();
            return displayName.includes(fallback) || fallback.includes(displayName);
        });
    }

    return null;
}

export function getBreakoutRoomNames(payload = {}) {
    return (payload.rooms || [])
        .map((room) => `${room.name || ""}`.trim())
        .filter(Boolean);
}

const normalizeRoomText = (value = "") =>
    `${value || ""}`
        .toLowerCase()
        .replace(/[^a-z0-9\s]/g, " ")
        .replace(/\s+/g, " ")
        .trim();

const tokenizeRoomText = (value = "") => normalizeRoomText(value).split(" ").filter(Boolean);

/**
 * Score how well a Zoom room name matches a university / target name.
 *
 * Zoom often truncates breakout titles, e.g.
 *   university: "University of North Carolina Wilmington"
 *   room:       "University of North Carolina"
 * Prefer the longest room name that is a prefix of the university name.
 */
export function scoreBreakoutRoomMatch(targetName = "", roomName = "") {
    const target = normalizeRoomText(targetName);
    const room = normalizeRoomText(roomName);
    if (!target || !room) {
        return 0;
    }
    if (target === room) {
        return 1;
    }

    const targetTokens = tokenizeRoomText(target);
    const roomTokens = tokenizeRoomText(room);
    if (!targetTokens.length || !roomTokens.length) {
        return 0;
    }

    // Classic Zoom truncation: room title is a leading prefix of the university name.
    // Example: "University of North Carolina Wilmington" → "University of North Carolina"
    if (target.startsWith(room)) {
        const coverage = room.length / target.length;
        // Require enough shared words so "University" alone does not win
        if (roomTokens.length >= 3 && coverage >= 0.45) {
            return Math.min(0.99, 0.88 + 0.11 * coverage);
        }
        if (roomTokens.length >= 2 && coverage >= 0.6) {
            return Math.min(0.97, 0.8 + 0.15 * coverage);
        }
    }

    // Whole-word prefix (handles mid-word Zoom cuts: "... Carol" vs "... Carolina")
    let sharedPrefix = 0;
    const prefixLimit = Math.min(targetTokens.length, roomTokens.length);
    while (sharedPrefix < prefixLimit && targetTokens[sharedPrefix] === roomTokens[sharedPrefix]) {
        sharedPrefix += 1;
    }
    // Last room token may be a cut prefix of the next university token
    if (
        sharedPrefix === roomTokens.length - 1 &&
        sharedPrefix < targetTokens.length &&
        targetTokens[sharedPrefix].startsWith(roomTokens[sharedPrefix]) &&
        roomTokens[sharedPrefix].length >= 4
    ) {
        sharedPrefix += 1;
    }
    if (sharedPrefix >= 3 && sharedPrefix === roomTokens.length) {
        const coverage = sharedPrefix / targetTokens.length;
        return Math.min(0.98, 0.86 + 0.12 * coverage);
    }

    // University shorter than room (e.g. "UNC" vs full title) — weaker than truncation
    if (room.startsWith(target) && targetTokens.length >= 3) {
        return 0.5 + 0.25 * (target.length / room.length);
    }

    const targetSet = new Set(targetTokens);
    const roomSet = new Set(roomTokens);
    let intersection = 0;
    roomSet.forEach((token) => {
        if (targetSet.has(token)) {
            intersection += 1;
        }
    });
    const union = new Set([...targetTokens, ...roomTokens]).size || 1;
    const jaccard = intersection / union;
    const prefixRatio = sharedPrefix / Math.max(targetTokens.length, roomTokens.length);

    // Prefer shared leading words (common for truncated university names)
    return Math.min(0.92, prefixRatio * 0.7 + jaccard * 0.35);
}

/**
 * Pick the best Zoom room for a university name when exact match is missing.
 * Returns { roomName, score } or null if nothing is confidently close enough.
 */
export function findBestBreakoutRoomMatch(roomNames = [], targetName = "", minScore = 0.55) {
    const target = `${targetName || ""}`.trim();
    if (!target || !roomNames.length) {
        return null;
    }

    const normalizedTarget = normalizeRoomText(target);

    let best = null;
    roomNames.forEach((roomName) => {
        const score = scoreBreakoutRoomMatch(target, roomName);
        const roomLen = `${roomName || ""}`.length;
        const isPrefixOfTarget = normalizedTarget.startsWith(normalizeRoomText(roomName));
        if (
            !best ||
            score > best.score ||
            // Same score: prefer longer room (more of the university name survived truncation)
            (score === best.score && roomLen > `${best.roomName}`.length) ||
            // Near-ties: still prefer the longer Zoom-truncation prefix
            (isPrefixOfTarget &&
                Math.abs(score - best.score) < 0.03 &&
                roomLen > `${best.roomName}`.length)
        ) {
            best = { roomName, score };
        }
    });

    if (!best || best.score < minScore) {
        return null;
    }
    return best;
}

export function breakoutRoomExists(roomNames = [], roomName = "") {
    return Boolean(findBestBreakoutRoomMatch(roomNames, roomName, 0.55));
}

/** Build public S3 occupancy URL: `{base}/{meetingNumber}.json`. */
export function buildOccupancyUrl(publicBaseUrl = "", meetingNumber = "") {
    const base = `${publicBaseUrl || ""}`.trim().replace(/\/$/, "");
    const mn = `${meetingNumber || ""}`.trim();
    if (!base || !mn) {
        return null;
    }
    return `${base}/${mn}.json`;
}

/**
 * Resolve All/Students counts for a university from occupancy snapshot rooms.
 * Uses fuzzy Zoom room-name matching (truncation-safe).
 * @returns {{ allCount: number, studentCount: number, roomName: string } | null}
 */
export function getOccupancyForUniversity(occupancy = null, universityName = "") {
    if (!occupancy || !occupancy.joined) {
        return null;
    }
    const rooms =
        (occupancy.summary && occupancy.summary.rooms) ||
        occupancy.rooms ||
        [];
    const roomNames = rooms
        .map((room) => `${(room && room.name) || ""}`.trim())
        .filter(Boolean);
    const match = findBestBreakoutRoomMatch(roomNames, universityName, 0.55);
    if (!match) {
        return null;
    }
    const room = rooms.find(
        (r) => `${(r && r.name) || ""}`.trim() === match.roomName
    );
    if (!room) {
        return null;
    }
    return {
        allCount: Number(room.allCount) || 0,
        studentCount: Number(room.studentCount) || 0,
        roomName: match.roomName,
    };
}

/**
 * Counselling desk accounts are typically named "Counselling Desk" (PU country Other).
 * Notes/move gate must not apply to those logins.
 */
export function isCounsellingDeskLogin(universityDetails = {}) {
    const name = `${universityDetails.universityName || ""}`.trim().toLowerCase();
    return name.includes("counselling") || name.includes("counseling");
}

/** Target PU row is counselling / Other category — skip notes requirement for Move. */
export function isCounsellingOrOtherUniversityRow(university = {}) {
    const name = `${university.university_name || university.universityName || ""}`
        .trim()
        .toLowerCase();
    const country = `${university.country || ""}`.trim().toLowerCase();
    if (country === "other") {
        return true;
    }
    return name.includes("counselling") || name.includes("counseling");
}

const normalizeNoteUniversityKey = (value = "") =>
    `${value || ""}`
        .toLowerCase()
        .replace(/&/g, " and ")
        .replace(/[^a-z0-9]+/g, " ")
        .trim()
        .replace(/\s+/g, " ");

/** True when Notes Provided has at least one note for this university name. */
export function studentHasNotesForUniversity(notes = [], universityName = "") {
    const target = normalizeNoteUniversityKey(universityName);
    if (!target || !Array.isArray(notes) || !notes.length) {
        return false;
    }
    return notes.some((note) => {
        const text = `${note.text || note.notes || ""}`.trim();
        if (!text) {
            return false;
        }
        return normalizeNoteUniversityKey(note.universityName || note.university_name) === target;
    });
}

/**
 * Can this login use Move to room?
 *
 * - Counselling Desk login → yes, any room.
 * - University login → if Notes Provided has a note for the logged-in university,
 *   Move is enabled for EVERY room; otherwise Move is disabled for all rooms.
 *   (Target row does not matter — only whether my university commented.)
 */
export function canBreakoutMoveToUniversity({
    universityDetails = {},
    // targetUniversity unused for university-login gate (any room once notes exist)
    targetUniversity, // eslint-disable-line no-unused-vars
    notes = [],
} = {}) {
    if (isCounsellingDeskLogin(universityDetails)) {
        return true;
    }

    return studentHasNotesForUniversity(
        notes,
        universityDetails.universityName
    );
}

/** @deprecated use canBreakoutMoveToUniversity */
export function shouldRequireNotesBeforeBreakoutMove({
    universityDetails = {},
    targetUniversity = {},
} = {}) {
    if (isCounsellingDeskLogin(universityDetails)) {
        return false;
    }
    if (isCounsellingOrOtherUniversityRow(targetUniversity)) {
        return false;
    }
    return true;
}

export function getBreakoutMoveNotesGateMessage(universityName = "") {
    const label = `${universityName || ""}`.trim() || "your university";
    return `Add notes for ${label} in Student Details before you can move this student to any room.`;
}

export function isParticipantAlreadyInRoom(participant, roomName = "") {
    if (!participant) {
        return false;
    }
    const current = `${participant.currentRoomName || ""}`.trim();
    const target = `${roomName || ""}`.trim();
    if (!current || !target) {
        return false;
    }
    if (current.toLowerCase() === target.toLowerCase()) {
        return true;
    }
    // Treat strong fuzzy match as same room (truncated Zoom titles)
    return scoreBreakoutRoomMatch(target, current) >= 0.75;
}

export function getBreakoutMoveErrorMessage(error, { toRoomName = "" } = {}) {
    const responseData = error && error.response && error.response.data;
    const detail = responseData && responseData.detail;
    const raw =
        (typeof detail === "string" && detail) ||
        (responseData && (responseData.error || responseData.message)) ||
        (error && error.message) ||
        "";
    const text = `${raw}`.toLowerCase();

    if (text.includes("participant not found")) {
        return "Participant was not found in the live Zoom meeting";
    }

    if (
        text.includes("already") ||
        text.includes("same room") ||
        text.includes("already in")
    ) {
        return toRoomName
            ? `Participant is already in ${toRoomName}`
            : "Participant is already in this room";
    }

    if (
        text.includes("room not found") ||
        (text.includes("room") && text.includes("not found"))
    ) {
        return toRoomName
            ? `Breakout room "${toRoomName}" was not found in the live meeting`
            : "Breakout room was not found in the live meeting";
    }

    return "Something went wrong while moving the participant. Please try again.";
}

const sleep = (ms) => new Promise((resolve) => setTimeout(resolve, ms));

/**
 * Wait for a queued move to finish.
 *
 * Loop every 5s: GET /jobs/{id}
 *   - succeeded → done
 *   - failed → stop with error
 *   - pending / queued → keep checking, but after maxPendingWaitMs (30s)
 *     abandon and unlock UI so the user can do another move
 *   - processing → keep checking a bit longer (Zoom move in progress)
 */
export async function waitForBreakoutMoveConfirmation({
    jobId,
    fetchJob,
    intervalMs = 5000,
    maxPendingWaitMs = 30000,
    maxProcessingWaitMs = 60000,
    maxFailures = 3,
    // Legacy optional args ignored — kept so older callers do not break
    meetingNumber, // eslint-disable-line no-unused-vars
    userId, // eslint-disable-line no-unused-vars
    toRoomName, // eslint-disable-line no-unused-vars
    fetchParticipants, // eslint-disable-line no-unused-vars
    maxAttempts, // eslint-disable-line no-unused-vars
    maxWaitMs, // eslint-disable-line no-unused-vars
}) {
    let lastJobError = "";
    let failures = 0;
    const pollEveryMs = Math.max(1000, intervalMs);
    const failureLimit = Math.max(1, Math.min(3, maxFailures));
    const pendingLimitMs = Math.max(pollEveryMs, maxPendingWaitMs);
    const processingLimitMs = Math.max(pendingLimitMs, maxProcessingWaitMs);
    const started = Date.now();
    // Hard stop so the loop cannot run forever if status is unexpected
    const hardDeadline = started + processingLimitMs + pollEveryMs;

    if (!jobId || !fetchJob) {
        return {
            ok: false,
            source: "request_failed",
            error: "Move was queued but job id is missing. Try Move again.",
        };
    }

    while (Date.now() < hardDeadline) {
        const elapsed = Date.now() - started;

        try {
            const jobResponse = await fetchJob(jobId);
            const job = (jobResponse && jobResponse.data) || {};
            const status = `${job.status || ""}`.toLowerCase();

            if (status === "succeeded") {
                return { ok: true, source: "job" };
            }
            if (status === "failed") {
                lastJobError = job.error || "Move failed";
                return { ok: false, source: "job", error: lastJobError };
            }

            // Still pending after 30s → leave this task, unlock UI
            if (
                (status === "pending" || status === "queued" || !status) &&
                elapsed >= pendingLimitMs
            ) {
                return {
                    ok: false,
                    source: "pending_timeout",
                    error:
                        "Move is still queued after 30s. You can try another move now — check Zoom if needed.",
                };
            }

            // Processing longer than allowed → unlock
            if (status === "processing" && elapsed >= processingLimitMs) {
                return {
                    ok: false,
                    source: "timeout",
                    error:
                        "Move is still running in Zoom. Check the room, then try again if needed.",
                };
            }

            await sleep(pollEveryMs);
        } catch (error) {
            const status = error && error.response && error.response.status;
            if (status === 404) {
                if (elapsed >= pendingLimitMs) {
                    return {
                        ok: false,
                        source: "pending_timeout",
                        error:
                            "Move is still queued after 30s. You can try another move now — check Zoom if needed.",
                    };
                }
                await sleep(pollEveryMs);
                continue;
            }
            failures += 1;
            lastJobError =
                (error.response &&
                    error.response.data &&
                    error.response.data.detail) ||
                error.message ||
                "Job status request failed";
            if (failures >= failureLimit) {
                return {
                    ok: false,
                    source: "request_failed",
                    error:
                        "Could not confirm the move. Check Zoom and try Move again.",
                };
            }
            await sleep(pollEveryMs);
        }
    }

    return {
        ok: false,
        source: "timeout",
        error:
            lastJobError ||
            "Move was not confirmed. Check Zoom, then try Move again.",
    };
}
